import { useMutation, useQueryClient } from '@tanstack/react-query'

import { authClient } from '@/lib/auth-client'
import {
  changePassword,
  confirmTwoFactorOtpSetup,
  confirmTwoFactorSetup,
  disableTwoFactor,
  enableTwoFactor,
  generateTwoFactorBackupCodes,
  sendTwoFactorOtp,
  updateProfileName,
} from '@/requests/auth'
import {
  deleteUserAvatar,
  updateTwoFactorNudgePreference,
  uploadUserAvatar,
} from '@/requests/users'

async function refreshSession() {
  /* Pull updated user record (avatar, name) into the session store so
     authClient.useSession() subscribers re-render with fresh data. */
  await authClient.getSession()
}

export function useUpdateProfileNameMutation() {
  return useMutation({
    mutationFn: async (name: string) => {
      const result = await updateProfileName(name)
      if (result.error) throw new Error(result.error.message)
      return result
    },
  })
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string
      newPassword: string
    }) => {
      const result = await changePassword(currentPassword, newPassword)
      if (result.error) throw new Error(result.error.message)
      return result
    },
  })
}

export function useUploadUserAvatarMutation() {
  return useMutation({
    mutationFn: async (file: File) => {
      const result = await uploadUserAvatar(file)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: refreshSession,
  })
}

export function useDeleteUserAvatarMutation() {
  return useMutation({
    mutationFn: async () => {
      const result = await deleteUserAvatar()
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: refreshSession,
  })
}

export function useEnableTwoFactorMutation() {
  return useMutation({
    mutationFn: async (password: string) => {
      const result = await enableTwoFactor(password)
      if (result.error) throw new Error(result.error.message)
      return result.data
    },
  })
}

export function useConfirmTwoFactorSetupMutation() {
  return useMutation({
    mutationFn: async (code: string) => {
      const result = await confirmTwoFactorSetup(code)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: refreshSession,
  })
}

export function useSendTwoFactorOtpMutation() {
  return useMutation({
    mutationFn: async () => {
      const result = await sendTwoFactorOtp()
      if (result.error) throw new Error(result.error.message)
      return result
    },
  })
}

export function useConfirmTwoFactorOtpSetupMutation() {
  return useMutation({
    mutationFn: async (code: string) => {
      const result = await confirmTwoFactorOtpSetup(code)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: refreshSession,
  })
}

export function useDisableTwoFactorMutation() {
  return useMutation({
    mutationFn: async (password: string) => {
      const result = await disableTwoFactor(password)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: refreshSession,
  })
}

export function useGenerateTwoFactorBackupCodesMutation() {
  return useMutation({
    mutationFn: async (password: string) => {
      const result = await generateTwoFactorBackupCodes(password)
      if (result.error) throw new Error(result.error.message)
      return result.data
    },
  })
}

export function useUpdateTwoFactorNudgePreferenceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (optedOut: boolean) => {
      const result = await updateTwoFactorNudgePreference(optedOut)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['two-factor-nudge-preference'] })
    },
  })
}
